/**
 * Truncate a `contenteditable` node in place, leaving the caret at the end.
 *
 * The server rejects anything longer, so the field refuses it first: caught at
 * the keystroke it is a full field, caught at the request it is an error nobody
 * can act on. Writing to `textContent` collapses the selection, hence the
 * range — without it the caret jumps to the start on the character that
 * overflows. Returns the text now in the node.
 */
export function clampText(el: HTMLElement, limit: number): string {
  const text = el.textContent ?? '';
  if (text.length <= limit) return text;

  // Never cut a surrogate pair in half: the limit counts UTF-16 units (so does
  // the DTO's @MaxLength, and the two must agree), but landing inside an emoji
  // would leave a broken glyph in the name. Drop the orphaned half instead.
  const head = text.slice(0, limit);
  const last = head.charCodeAt(head.length - 1);
  const cut = last >= 0xd800 && last <= 0xdbff ? head.slice(0, -1) : head;
  el.textContent = cut;

  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  return cut;
}
